import { useCallback } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  CopyIcon,
  DropdownMenu,
  ExternalLinkIcon,
  RefreshCwIcon,
  toast,
  Tooltip,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

import { useShouldShowDeveloperData } from './useShouldShowDeveloperData'

export { useShouldShowDeveloperData } from './useShouldShowDeveloperData'

const HQ_URLS = {
  post: (id: string): string => `https://hq.buffertools.com/posts/${id}`,
  channel: (id: string): string => `https://hq.buffertools.com/channels/${id}`,
  organization: (id: string): string =>
    `https://hq.buffertools.com/organizations/${id}`,
}

interface CopyIdMenuItemProps {
  label: string
  id: string
  toastMessage?: string
}

export function CopyIdMenuItem({
  label,
  id,
  toastMessage,
}: CopyIdMenuItemProps): JSX.Element {
  return (
    <Tooltip content={id}>
      <DropdownMenu.Item
        onSelect={(): void => {
          void navigator.clipboard
            .writeText(id)
            .then(() =>
              toast.success(toastMessage ?? `${label} copied to clipboard`, {
                duration: 2000,
              }),
            )
            .catch(() => toast.error('Failed to copy to clipboard'))
        }}
      >
        <CopyIcon />
        {label}
      </DropdownMenu.Item>
    </Tooltip>
  )
}

interface OpenInHQMenuItemProps {
  label: string
  type: 'post' | 'channel' | 'organization'
  id: string
}

export function OpenInHQMenuItem({
  label,
  type,
  id,
}: OpenInHQMenuItemProps): JSX.Element {
  const url = HQ_URLS[type](id)

  return (
    <DropdownMenu.Item asChild>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <ExternalLinkIcon />
        {label}
      </a>
    </DropdownMenu.Item>
  )
}

const backfillChannelAnalyticsMutation = graphql(/* GraphQL */ `
  mutation BackfillChannelAnalytics($input: BackfillChannelAnalyticsInput!) {
    backfillChannelAnalytics(input: $input) {
      __typename
      ... on VoidMutationError {
        message
      }
    }
  }
`)

interface ChannelDeveloperDataMenuItemsProps {
  channelId: string
  serviceId?: string
  showBackfillAnalytics?: boolean
  withSeparator?: boolean
}

export function ChannelDeveloperDataMenuItems({
  channelId,
  serviceId,
  showBackfillAnalytics = false,
  withSeparator = false,
}: ChannelDeveloperDataMenuItemsProps): JSX.Element | null {
  const shouldShowDeveloperData = useShouldShowDeveloperData()
  const [backfillAnalytics] = useMutation(backfillChannelAnalyticsMutation)

  const handleBackfill = useCallback(async (): Promise<void> => {
    try {
      const { data } = await backfillAnalytics({
        variables: { input: { channelId } },
      })
      const result = data?.backfillChannelAnalytics
      if (result?.__typename === 'VoidMutationError') {
        toast.error(result.message)
      } else if (result?.__typename === 'BackfillChannelAnalyticsSuccess') {
        toast.success('Channel analytics backfill triggered')
      } else {
        toast.error('Failed to trigger channel analytics backfill')
      }
    } catch {
      toast.error('Failed to trigger channel analytics backfill')
    }
  }, [backfillAnalytics, channelId])

  if (!shouldShowDeveloperData) {
    return null
  }

  return (
    <>
      {withSeparator && <DropdownMenu.Separator />}
      <DropdownMenu.Label>Developer Data</DropdownMenu.Label>
      <CopyIdMenuItem
        label="Copy Channel ID"
        id={channelId}
        toastMessage="Channel ID copied to clipboard!"
      />
      {serviceId && (
        <CopyIdMenuItem
          label="Copy Service ID"
          id={serviceId}
          toastMessage="Service ID copied to clipboard!"
        />
      )}
      <OpenInHQMenuItem label="Open in HQ" type="channel" id={channelId} />
      {showBackfillAnalytics && (
        <DropdownMenu.Item
          onSelect={(): void => {
            handleBackfill().catch(() => undefined)
          }}
        >
          <RefreshCwIcon />
          Backfill Analytics
        </DropdownMenu.Item>
      )}
    </>
  )
}
